// create same filters form like admin but by using brand side components
import type { FunctionComponent } from "react";
import { Controller } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import type { IGenreMinimal } from "../../../../types/models/genre.models.types";
import { SocialGenderOptions } from "../../../../partials/admin/campaign-form.admin.partials";
import Button from "../../../components/core/button.core";
import { z } from "zod";
import { SocialGender } from "../../../../types/models/social.models.types";
import { useParams } from "react-router-dom";
import Input from "../../../components/core/input.core";
import { MultiSelect } from "../../../components/core/multiselect.core";
import { MultiSelectGenre } from "../../../components/core/multiselect-genre.core";
import useProposalSocialStore from "../../../store/proposal-social.store";
import { useToast } from "../../../../hooks/use-toast";
import { useProposalSocialApplyFilterHttp } from "../../../hooks/http/proposal-socials/proposal-social-apply-filter.hook";
import { handleHttpError } from "../../../../utils/utils";
import { useGetSocialGenreHttp } from "../../../hooks/http/social-genre/get-social-genres.hook";

// declear form schema

const FormSchema = z.object({
  filters: z.object({
    followers: z
      .object({
        min: z.coerce
          .number()
          .min(1, { message: "Please enter minimum followers" })
          .optional(),
        max: z.coerce
          .number()
          .min(1, { message: "Please enter maximum followers" })
          .optional(),
      })
      .optional(),
    gender: z.array(z.nativeEnum(SocialGender)).optional(),
    genre: z
      .array(
        z.object({
          id: z.string(),
          title: z.string(),
          emoji: z.string(),
        })
      )
      .optional(),
  }),
});

type IzManageFiltersForm = z.infer<typeof FormSchema>;

export const ManageFiltersPartial: FunctionComponent = () => {
  const { setData: setProposalSocials } = useProposalSocialStore();
  const { toast } = useToast();
  const { proposalId } = useParams();
  const [genreOptions, setGenreOptions] = useState<IGenreMinimal[]>([]);

  const { execute: getGenreOptionsHttp, fetching: fetchingGenres } =
    useGetSocialGenreHttp();

  const { execute: applyFilterHttp, fetching: fetchingApplyFilter } =
    useProposalSocialApplyFilterHttp();

  async function onGenreFetch() {
    const response = await getGenreOptionsHttp({
      body: null,
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setGenreOptions(response.data.genres);
  }

  async function applyFilters(data: IzManageFiltersForm) {
    if (!proposalId) {
      return;
    }
    const response = await applyFilterHttp({
      body: {
        filter: {
          followers:
            data.filters?.followers?.max || data.filters?.followers?.min
              ? {
                  min: data.filters?.followers
                    ? data.filters.followers.min
                    : undefined,
                  max: data.filters?.followers
                    ? data.filters.followers.max
                    : undefined,
                }
              : undefined,
          gender:
            data.filters.gender && data.filters.gender.length > 0
              ? data.filters.gender.map((g: { value: string } | string) =>
                  typeof g === "string" ? g : g.value
                )
              : undefined,
          genre:
            data.filters.genre && data.filters.genre.length > 0
              ? data.filters.genre.map((genre) => genre.id)
              : undefined,
        },
      },
      pathParams: { proposalId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setProposalSocials(response.data.campaignProposalSocials);
    toast({
      content: "Filters applied successfully",
      toastType: "success",
    });
    document.getElementById("closeSheet")?.click();
  }

  const { handleSubmit, control } = useForm<IzManageFiltersForm>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      filters: {
        followers: undefined,
        gender: [],
        genre: [],
      },
    },
  });

  const { isDirty } = useFormState({ control });

  const onSubmit = async (data: IzManageFiltersForm) => {
    await applyFilters(data);
  };

  return (
    <div className="relative flex flex-col w-full h-full font-cabinetgrotesk text-soloprimary">
      <div className="top-0 sticky flex items-center gap-5 mb-6 px-4 py-5 border-b border-black w-full">
        <p className="font-semibold text-xl">Manage filters</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 px-4 pb-10">
        <div className="flex md:flex-row flex-col items-center gap-4">
          <Controller
            name="filters.followers.min"
            control={control}
            render={({ field }) => (
              <Input
                label="Minimum followers"
                placeholder="Enter minimum followers"
                type="number"
                {...field}
              />
            )}
          />
          <Controller
            name="filters.followers.max"
            control={control}
            render={({ field }) => (
              <Input
                label="Maximum followers"
                placeholder="Enter maximum followers"
                type="number"
                {...field}
              />
            )}
          />
        </div>

        <Controller
          name="filters.gender"
          control={control}
          render={({ field }) => (
            <div className="space-y-1 md:space-y-2">
              <p className="font-semibold text-base md:text-lg">
                Select gender preferences
              </p>
              <MultiSelect
                className="py-6 pl-4 border border-black rounded-xl font-semibold placeholder:text-tertiary resize-none"
                placeholder={"Select gender preferences"}
                defaultValue={field.value ? field.value : []}
                {...field}
                options={Object.values(SocialGenderOptions).map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))}
                onValueChange={(e) => {
                  field.onChange(e);
                }}
              />
            </div>
          )}
        />
        <Controller
          name="filters.genre"
          control={control}
          render={({ field }) => (
            <div className="space-y-1 md:space-y-2">
              <p className="font-semibold text-base md:text-lg">
                Select content genres
              </p>
              <MultiSelectGenre
                fetcher={onGenreFetch}
                fetching={fetchingGenres}
                className="py-6 pl-4 border border-black rounded-xl font-semibold placeholder:text-secondary resize-none"
                placeholder="Select content genres"
                defaultValue={field.value ? field.value : []}
                value={field.value ? field.value.map((g) => g.id) : []}
                options={[
                  ...genreOptions.map((option) => ({
                    value: option.id,
                    label: option.title,
                    icon: option.emoji,
                  })),
                ]}
                onValueChange={(selected) => {
                  field.onChange(Array.isArray(selected) ? selected : []);
                }}
              />
            </div>
          )}
        />

        <div className="bottom-0 left-0 absolute flex justify-end px-4 py-4 w-full">
          <div className="w-full">
            <Button
              type="submit"
              disabled={!isDirty}
              isLoading={fetchingApplyFilter}
              className="w-full font-semibold text-black"
            >
              Update filters
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
